import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { beautifyNumber } from '../../../Utils/FormatNumber.js';
import SelfIncomesService from '../../../Services/selfIncomes.service.js';
import EmailModal from './EmailModal.js';
import WhatsAppModal from './WhatsAppModal.js';
import '../../../pages/Document/Document.scss';

const StyledDataGrid = styled(DataGrid)(() => ({
  width: '100%',
  '& *': {
    boxSizing: 'border-box !important',
  },
  '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
    backgroundColor: '#E3E7FE',
  },
  '& .MuiDataGrid-columnHeader': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 8px',
    minHeight: '44px !important',
    maxHeight: '44px !important',
    height: '44px !important',
    boxSizing: 'border-box',
  },
  '& .MuiDataGrid-columnHeaders': {
    minHeight: '44px !important',
    maxHeight: '44px !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: '#1222b3',
    fontFamily: 'Montserrat',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 'normal',
    textAlign: 'center',
  },
  '& .MuiDataGrid-sortIcon': {
    opacity: '0.8 !important',
    color: '#1222b3',
  },
  '& .MuiDataGrid-menuIconButton': {
    color: '#1222b3',
  },
  '& .MuiDataGrid-cell': {
    borderTop: '1px solid rgba(191, 191, 193, 0.56)',
    backgroundColor: 'rgba(234, 237, 255, 0.32)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 8px',
    fontSize: '13px',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(191, 200, 255, 0.3)',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-footerContainer': {
    minHeight: 42,
  },
}));

const summaryCardSx = {
  px: 1.5,
  py: 0.75,
  minWidth: 110,
  border: '1px solid rgba(48, 79, 255, 0.2)',
  borderRadius: '8px',
  background: 'rgba(48, 79, 255, 0.06)',
};

const actionBtnSx = {
  textTransform: 'none',
  height: 'var(--xtram-control-height, 40px)',
  minHeight: 'var(--xtram-control-height, 40px)',
  fontSize: 'var(--xtram-text-sm, 13px)',
  whiteSpace: 'nowrap',
  borderRadius: '8px',
  fontWeight: 600,
  boxSizing: 'border-box',
  px: 1.5,
};

const formatDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = (`0${date.getDate()}`).slice(-2);
  const month = (`0${date.getMonth() + 1}`).slice(-2);
  return `${day}/${month}/${date.getFullYear()}`;
};

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const SelfInvoicesTab = ({
  client,
  token,
  direction,
  isRange,
  sortDate,
  startDate,
  endDate,
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [detailsRow, setDetailsRow] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [whatsAppOpen, setWhatsAppOpen] = useState(false);
  const [targetEmails, setTargetEmails] = useState([]);
  const [targetPhones, setTargetPhones] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const resolveDates = useCallback(() => {
    if (isRange) {
      return {
        date: startDate ? new Date(startDate).toISOString() : null,
        endDate: endDate ? new Date(endDate).toISOString() : null,
      };
    }
    const selected = sortDate
      ? new Date(Date.UTC(sortDate.getFullYear(), sortDate.getMonth(), 10))
      : new Date();
    return { date: selected.toISOString(), endDate: selected.toISOString() };
  }, [isRange, sortDate, startDate, endDate]);

  const loadData = useCallback(async () => {
    if (!client?.caseId || !token) return;
    setLoading(true);
    try {
      const { date, endDate: end } = resolveDates();
      const data = await SelfIncomesService.GetSelfInvoicesByDateRange(
        client.caseId,
        date,
        end,
        token
      );
      setRows(Array.isArray(data) ? data : []);
      setSelectionModel([]);
    } catch (error) {
      console.error(error);
      toast.error(t('selfInvoicesLoadError'));
    } finally {
      setLoading(false);
    }
  }, [client?.caseId, token, resolveDates, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.reference, r.description, r.accountCodeName, r.accountCaseId, String(r.uploadNumber), String(r.totalAmount)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, searchQuery]);

  const summary = useMemo(() => {
    const list = filteredRows;
    return {
      count: list.length,
      totalNet: list.reduce((s, r) => s + (Number(r.netAmount) || 0), 0),
      totalVat: list.reduce((s, r) => s + (Number(r.vatAmount) || 0), 0),
      totalWithVat: list.reduce((s, r) => s + (Number(r.totalAmount) || 0), 0),
    };
  }, [filteredRows]);

  const selectedRows = useMemo(
    () => filteredRows.filter((r) => selectionModel.some((id) => String(id) === String(r.expenseId))),
    [filteredRows, selectionModel]
  );

  const handleDownloadPdf = async (row) => {
    if (!row?.documentUrl) {
      toast.error(t('noDocumentAvailable'));
      return;
    }
    try {
      const res = await fetch(row.documentUrl);
      const blob = await res.blob();
      downloadBlob(blob, `SelfInvoice_${row.reference || row.uploadNumber}.pdf`);
    } catch (error) {
      console.error(error);
      toast.error(t('downloadFailed'));
    }
  };

  const handleOpenPdf = (row) => {
    if (!row?.documentUrl) {
      toast.error(t('noDocumentAvailable'));
      return;
    }
    window.open(row.documentUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadReport = async () => {
    if (!client?.caseId || !token) return;
    setLoading(true);
    try {
      const { date, endDate: end } = resolveDates();
      const blob = await SelfIncomesService.ExportSelfInvoiceReportPdf(
        client.caseId,
        date,
        end,
        token
      );
      downloadBlob(blob, `SelfInvoices_Report_${client.caseId}.pdf`);
      toast.success(t('selfInvoiceReportDownloaded'));
    } catch (error) {
      console.error(error);
      toast.error(t('selfInvoiceReportFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (rowsToSend, emails) => {
    const recipients = Array.isArray(emails) ? emails : [emails].filter(Boolean);
    try {
      await SelfIncomesService.SendSelfInvoiceEmail(
        {
          caseId: client.caseId,
          accountantId: client.accountantId,
          toEmails: recipients,
          documentIds: rowsToSend.map((r) => r.expenseId),
        },
        token
      );
      toast.success(t('BulkEmailSent'));
      setEmailOpen(false);
      setTargetEmails([]);
    } catch (error) {
      console.error(error);
      toast.error(`${t('BulkEmailFailed')}: ${error.message}`);
    }
  };

  const handleSendWhatsApp = async (rowsToSend, phones) => {
    const recipients = Array.isArray(phones) ? phones : [phones].filter(Boolean);
    try {
      await SelfIncomesService.SendSelfInvoiceWhatsApp(
        {
          caseId: client.caseId,
          accountantId: client.accountantId,
          toPhones: recipients,
          documentIds: rowsToSend.map((r) => r.expenseId),
        },
        token
      );
      toast.success(t('BulkWhatsAppSent') || t('BulkEmailSent'));
      setWhatsAppOpen(false);
      setTargetPhones([]);
    } catch (error) {
      console.error(error);
      toast.error(t('BulkWhatsAppFailed') || error.message);
    }
  };

  const columns = [
      {
        field: 'documentDate',
        headerName: t('Date'),
        headerAlign: 'center',
        align: 'center',
        flex: 0.8,
        minWidth: 95,
        renderCell: (params) => formatDate(params.row.documentDate ?? params.value),
      },
      {
        field: 'reference',
        headerName: t('Reference'),
        headerAlign: 'center',
        align: 'center',
        flex: 0.9,
        minWidth: 90,
      },
      {
        field: 'accountCodeName',
        headerName: t('Supplier') || t('AccountName'),
        headerAlign: 'center',
        align: 'center',
        flex: 1.1,
        minWidth: 120,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} arrow>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {params.value}
            </span>
          </Tooltip>
        ),
      },
      {
        field: 'description',
        headerName: t('Description'),
        headerAlign: 'center',
        align: 'center',
        flex: 1.2,
        minWidth: 130,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} arrow>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {params.value}
            </span>
          </Tooltip>
        ),
      },
      {
        field: 'netAmount',
        headerName: t('NetAmount') || t('Amount'),
        headerAlign: 'center',
        align: 'center',
        flex: 0.8,
        minWidth: 95,
        renderCell: (params) => beautifyNumber(params.row.netAmount ?? params.value),
      },
      {
        field: 'vatAmount',
        headerName: t('VAT'),
        headerAlign: 'center',
        align: 'center',
        flex: 0.7,
        minWidth: 85,
        renderCell: (params) => beautifyNumber(params.row.vatAmount ?? params.value),
      },
      {
        field: 'totalAmount',
        headerName: t('Total'),
        headerAlign: 'center',
        align: 'center',
        flex: 0.8,
        minWidth: 95,
        renderCell: (params) => beautifyNumber(params.row.totalAmount ?? params.value),
      },
      {
        field: 'uploadNumber',
        headerName: t('UploadNumber'),
        headerAlign: 'center',
        align: 'center',
        flex: 0.7,
        minWidth: 85,
      },
      {
        field: 'actions',
        headerName: t('Actions'),
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterable: false,
        width: 150,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip title={t('View') || t('Details')}>
              <IconButton size="small" onClick={() => setDetailsRow(params.row)} sx={{ color: '#304FFF', p: 0.4 }}>
                <VisibilityIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('Download')}>
              <IconButton size="small" onClick={() => handleDownloadPdf(params.row)} sx={{ color: '#1976d2', p: 0.4 }}>
                <DownloadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('SendByEmail')}>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectionModel([String(params.row.expenseId)]);
                  setTargetEmails(client?.email ? [client.email] : []);
                  setEmailOpen(true);
                }}
                sx={{ color: '#EA4335', p: 0.4 }}
              >
                <EmailIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('SendByWhatsApp')}>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectionModel([String(params.row.expenseId)]);
                  setTargetPhones(client?.phoneNumber ? [client.phoneNumber] : []);
                  setWhatsAppOpen(true);
                }}
                sx={{ color: '#25D366', p: 0.4 }}
              >
                <WhatsAppIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ];

  return (
    <Box sx={{ mt: 0.5, width: '100%' }}>
      <div className={`searchFilter-bar__container document-toolbar-row ${direction || ''}`}>
        <Box className="document-filter-row" dir={direction || 'rtl'} sx={{ width: '100%', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {[
            { label: t('selfInvoiceCount'), value: summary.count },
            { label: t('selfInvoiceTotalNet'), value: beautifyNumber(summary.totalNet) },
            { label: t('selfInvoiceTotalVat'), value: beautifyNumber(summary.totalVat) },
            { label: t('selfInvoiceTotalWithVat'), value: beautifyNumber(summary.totalWithVat) },
          ].map((card) => (
            <Paper key={card.label} elevation={0} sx={summaryCardSx}>
              <Typography variant="caption" sx={{ color: '#696665', fontSize: '0.7rem', display: 'block', lineHeight: 1.2 }}>
                {card.label}
              </Typography>
              <Typography sx={{ color: '#1222b3', fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
                {card.value}
              </Typography>
            </Paper>
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
          }}
          dir={direction}
        >
          <TextField
            size="small"
            dir={direction}
            placeholder={t('searchDocuments')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="document-search-bar"
            variant="standard"
            InputProps={{
              disableUnderline: true,
              className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="document-search-icon" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<AssessmentIcon />}
            onClick={handleDownloadReport}
            disabled={loading}
            sx={{ ...actionBtnSx, backgroundColor: '#304FFF', '&:hover': { backgroundColor: '#2539d4' } }}
          >
            {t('downloadSelfInvoiceReport')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EmailIcon sx={{ color: selectionModel.length ? '#EA4335' : undefined }} />}
            disabled={selectionModel.length === 0}
            onClick={() => {
              setTargetEmails(client?.email ? [client.email] : []);
              setEmailOpen(true);
            }}
            sx={{
              ...actionBtnSx,
              color: '#304FFF',
              borderColor: 'rgba(48, 79, 255, 0.35)',
              '&:hover': { borderColor: '#304FFF', backgroundColor: 'rgba(48, 79, 255, 0.06)' },
            }}
          >
            {t('SendByEmail')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<WhatsAppIcon sx={{ color: selectionModel.length ? '#25D366' : undefined }} />}
            disabled={selectionModel.length === 0}
            onClick={() => {
              setTargetPhones(client?.phoneNumber ? [client.phoneNumber] : []);
              setWhatsAppOpen(true);
            }}
            sx={{
              ...actionBtnSx,
              color: '#304FFF',
              borderColor: 'rgba(48, 79, 255, 0.35)',
              '&:hover': { borderColor: '#304FFF', backgroundColor: 'rgba(48, 79, 255, 0.06)' },
            }}
          >
            {t('SendByWhatsApp')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon sx={{ color: selectionModel.length ? '#304FFF' : undefined }} />}
            disabled={selectionModel.length === 0}
            onClick={async () => {
              for (const row of selectedRows) {
                await handleDownloadPdf(row);
              }
            }}
            sx={{
              ...actionBtnSx,
              color: '#304FFF',
              borderColor: 'rgba(48, 79, 255, 0.35)',
              '&:hover': { borderColor: '#304FFF', backgroundColor: 'rgba(48, 79, 255, 0.06)' },
            }}
          >
            {t('Download')}
          </Button>
        </Box>
        </Box>
      </div>

      <Box className={direction || ''} sx={{ height: '55vh', width: '100%', minHeight: 360 }}>
        <StyledDataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => String(row.expenseId)}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          density="compact"
          rowHeight={42}
          columnHeaderHeight={48}
          selectionModel={selectionModel}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(model) => setSelectionModel((model || []).map(String))}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          localeText={{ noRowsLabel: t('noSelfInvoices') }}
        />
      </Box>

      <Dialog open={Boolean(detailsRow)} onClose={() => setDetailsRow(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
          {t('selfInvoiceDetails')}
          <IconButton onClick={() => setDetailsRow(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1.5 }}>
          {detailsRow && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 1.5,
                alignItems: 'stretch',
                minHeight: 480,
                direction: 'rtl',
              }}
            >
              <Box
                sx={{
                  flex: { xs: '1 1 auto', md: '1 1 58%' },
                  minWidth: 0,
                  height: { xs: 360, md: 520 },
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5',
                }}
              >
                {detailsRow.documentUrl ? (
                  <iframe
                    title="self-invoice-pdf"
                    src={detailsRow.documentUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">{t('noDocumentAvailable')}</Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  flex: { xs: '1 1 auto', md: '0 0 40%' },
                  minWidth: { md: 280 },
                  maxWidth: { md: '42%' },
                  overflow: 'auto',
                  direction: direction || 'rtl',
                }}
              >
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e8e8e8', borderRadius: 1 }}>
                  <Table size="small">
                    <TableBody>
                      {[
                        [t('Date'), formatDate(detailsRow.documentDate)],
                        [t('Reference'), detailsRow.reference],
                        [t('Supplier') || t('AccountName'), detailsRow.accountCodeName],
                        [t('Description'), detailsRow.description],
                        [t('UploadNumber'), detailsRow.uploadNumber],
                        [t('NetAmount') || t('Amount'), beautifyNumber(detailsRow.netAmount)],
                        [t('VAT'), beautifyNumber(detailsRow.vatAmount)],
                        [t('Total'), beautifyNumber(detailsRow.totalAmount)],
                        [t('SortCode'), detailsRow.sortCodeId],
                        [t('ConfirmationNumber'), detailsRow.confirmationNumber],
                        [t('DebitAccount'), detailsRow.debitAccount],
                        [t('CreditAccount'), detailsRow.creditAccount],
                      ].map(([label, value]) => (
                        <TableRow key={label}>
                          <TableCell sx={{ fontWeight: 600, width: '40%', py: 0.75 }}>{label}</TableCell>
                          <TableCell sx={{ py: 0.75 }}>{value || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => detailsRow && handleOpenPdf(detailsRow)}>{t('Open')}</Button>
          <Button onClick={() => detailsRow && handleDownloadPdf(detailsRow)} startIcon={<DownloadIcon />}>
            {t('Download')}
          </Button>
          <Button onClick={() => setDetailsRow(null)}>{t('Close')}</Button>
        </DialogActions>
      </Dialog>

      <EmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        onSend={handleSendEmail}
        targetEmails={targetEmails}
        setTargetEmails={setTargetEmails}
        filteredData={filteredRows.map((r) => ({ ...r, id: String(r.expenseId), expenseId: r.expenseId }))}
        selectionModel={selectionModel.map(String)}
      />

      <WhatsAppModal
        open={whatsAppOpen}
        onClose={() => setWhatsAppOpen(false)}
        onSend={handleSendWhatsApp}
        targetPhones={targetPhones}
        setTargetPhones={setTargetPhones}
        filteredData={filteredRows.map((r) => ({ ...r, id: String(r.expenseId), expenseId: r.expenseId }))}
        selectionModel={selectionModel.map(String)}
      />
    </Box>
  );
};

export default SelfInvoicesTab;
